import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

export const PageHero = ({ eyebrow, title, italicWord, subtitle, crumb, image, cta }) => {
  const hasImage = Boolean(image);
  return (
    <section
      data-testid="page-hero"
      className="relative pt-28 pb-12 sm:pt-36 sm:pb-16 md:pt-40 md:pb-20 bg-white overflow-hidden border-b border-navy-100"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-20 -right-20 w-[32rem] h-[32rem] bg-navy-500/[0.04] blur-3xl rounded-full" />
      </div>

      <div className="container-edge relative z-10">
        <nav className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-navy-500 mb-8" aria-label="breadcrumb">
          <Link to="/" className="hover:text-red-500 transition-colors" data-testid="breadcrumb-home">Accueil</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-navy-700">{crumb || title}</span>
        </nav>

        <div className={`grid gap-10 lg:gap-16 items-center ${hasImage ? "lg:grid-cols-12" : ""}`}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className={hasImage ? "lg:col-span-7" : "max-w-3xl"}
          >
            {eyebrow && <div className="overline text-navy-500 mb-5">{eyebrow}</div>}
            <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-navy-700 leading-[1.08]">
              {title}
              {italicWord && <> <em className="red-shine not-italic">{italicWord}</em></>}
            </h1>
            {subtitle && (
              <p className="mt-5 sm:mt-6 text-[#555] text-sm sm:text-base md:text-lg max-w-2xl leading-relaxed">
                {subtitle}
              </p>
            )}
            {cta && (
              <div className="mt-8">
                {cta}
              </div>
            )}
          </motion.div>

          {hasImage && (
            <motion.div
              initial={{ opacity: 0, x: 30, scale: 0.96 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="lg:col-span-5 relative"
              data-testid="page-hero-image"
            >
              {/* Decorative corner brackets */}
              <div className="absolute -top-3 -left-3 w-12 h-12 border-t-2 border-l-2 border-red-500 z-10" />
              <div className="absolute -bottom-3 -right-3 w-12 h-12 border-b-2 border-r-2 border-navy-500 z-10" />
              <div className="relative aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5] overflow-hidden shadow-[0_30px_60px_-20px_rgba(0,35,98,0.30)]">
                <img
                  src={image}
                  alt={crumb || title}
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};

export default PageHero;
